import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssetCondition,
  AssetTag,
  ClosedownFile,
  ClosedownFileType,
  ClosedownJobDetail,
  ClosedownMode,
  ClosedownOption,
  ClosedownPayload,
  ClosedownStatus,
  EquipmentStatus,
  JobUpgrade,
  PurchaseOrder,
  RootCause,
  StatusAffirmation
} from '@app/models/closedown-detail.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { JobService } from '@app/services/job/job.service';
import { CommonFileStorageService } from '@app/services/commonfilestorage/commonfilestorage.service';
import { NavigationService } from '@app/services/navigation/navigation.service';
import { CustomValidators } from '@app/shared/custom-validators';
import { FileUploadTypeCode, JobType, UserType } from '@app/shared/enums';
import { AuthService } from '@app/services/auth/auth.service';
import { Company } from '@app/models/user.model';
import { catchError, firstValueFrom, forkJoin, Observable, of, switchMap } from 'rxjs';
import { ClosedownFileTypeKey } from '@app/shared/enums';
import { ConfigurationService } from '@app/services/configuration/configuration.service';
import { LocalizationService } from '../internationalization/localization.service';
import { SiteApiService } from '@app/services/site/site.api.service';
import { FileNote, FileStorageInfo, FileUploadRequest } from '@app/models/commonfilestorage.model';
import { UtilsService } from '@app/services/util/util-service';
import { HostListener } from '@angular/core';
import { PartnerConfiguration } from '@app/models/partnerconfiguration.model';

type DurationControlName = 'maintenanceTime' | 'travelTime';
type TimePickerControlName = DurationControlName | 'onsiteTime';
type DurationPart = 'hours' | 'minutes';

@Component({
  selector: 'app-open-job-detail',
  templateUrl: './open-job-detail.component.html',
  styleUrls: ['./open-job-detail.component.scss'],
  standalone: false,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: DateAdapter, useClass: NativeDateAdapter, deps: [MAT_DATE_LOCALE] }
  ]
})
export class OpenJobDetailComponent implements OnInit, OnDestroy {
  readonly durationFields: readonly { control: DurationControlName; label: string }[] = [
    { control: 'maintenanceTime', label: 'open_job_detail_maintenance_time' },
    { control: 'travelTime', label: 'open_job_detail_travel_time' }
  ];
  readonly durationHours = Array.from({ length: 24 }, (_, value) => String(value).padStart(2, '0'));
  readonly durationMinuteInterval = 5;
  readonly durationMinutes = Array.from(
    { length: 60 / this.durationMinuteInterval },
    (_, value) => String(value * this.durationMinuteInterval).padStart(2, '0')
  );
  readonly activeDuration = signal<TimePickerControlName | null>(null);
  readonly mode: ClosedownMode = this.route.snapshot.data['closedownMode'] === 'technician' ? 'technician' : 'admin';
  readonly isTechnician = this.mode === 'technician';
  private readonly technicianAuth = this.isTechnician ? inject(AuthService) : null;
  private readonly destroyRef = inject(DestroyRef);
  private technicianCompanies: readonly Company[] = [];
  readonly technicianAllocationType = signal<'internal' | 'external' | null>(null);
  readonly technicianAllocationForm = this.formBuilder.nonNullable.group({
    email: [{ value: '', disabled: true }],
    companyName: [{ value: '', disabled: true }],
    technicianName: [{ value: '', disabled: true }],
    phoneNumber: [{ value: '', disabled: true }]
  });
  technicianPendingApproval = false;
  readonly draftDurationHours = signal('00');
  readonly draftDurationMinutes = signal('00');

  get onsiteTimeDisplay(): string {
    const value = this.closedownForm.controls.onsiteTime.value;
    if (!value) {
      return '';
    }
    const [hours = '00', minutes = '00'] = value.split(':');
    const hour = Number(hours);
    return `${String(hour % 12 || 12).padStart(2, '0')}:${minutes} ${hour < 12 ? 'AM' : 'PM'}`;
  }

  get jobTypeKey(): string {
    const keys: Record<string, string> = {
      NEW: 'open_jobs_summary_new',
      'ON-SITE': 'open_jobs_summary_on_site',
      RETURNING: 'open_jobs_summary_returning',
      'AWAITING-PARTS': 'open_jobs_summary_awaiting_parts',
      PPM: 'open_jobs_summary_ppm'
    };
    return keys[this.jobType.toUpperCase()] || 'open_jobs_summary_title';
  }

  openDurationPicker(controlName: TimePickerControlName): void {
    this.draftDurationHours.set(this.getDurationPart(controlName, 'hours'));
    this.draftDurationMinutes.set(this.getDurationPart(controlName, 'minutes'));
    this.activeDuration.set(controlName);
  }

  clearDurationPicker(): void {
    this.draftDurationHours.set('00');
    this.draftDurationMinutes.set('00');
  }

  confirmDurationPicker(controlName: TimePickerControlName): void {
    const control = this.closedownForm.controls[controlName];
    control.setValue(`${this.draftDurationHours()}:${this.draftDurationMinutes()}`);
    control.markAsDirty();
    control.markAsTouched();
    this.activeDuration.set(null);
  }

  setOnsiteDateToToday(): void {
    const control = this.closedownForm.controls.onsiteDate;
    control.setValue(new Date());
    control.markAsDirty();
    control.markAsTouched();
  }

  setOnsiteTimeToNow(): void {
    const control = this.closedownForm.controls.onsiteTime;
    control.setValue(this.formatTime(new Date(), this.durationMinuteInterval));
    control.markAsDirty();
    control.markAsTouched();
  }

  readonly closedownForm = this.formBuilder.group({
    assetTag: [''],
    rootCause: [null as string | null, Validators.required],
    gasUsed: [false],
    gasType: [null as string | null],
    gasUsage: [0, Validators.min(0)],
    gasLeakType: [null as string | null],
    status: [null as string | null, Validators.required],
    statusSubType: [null as string | null, Validators.required],
    assetOnDeparture: [null as string | null],
    equipmentStatus: [null as string | null],
    notes: [''],
    onsiteDate: [new Date() as Date | null, Validators.required],
    onsiteTime: ['00:00', Validators.required],
    maintenanceTime: ['00:00', [Validators.required, CustomValidators.isTimeValid()]],
    travelTime: ['00:00', [Validators.required, CustomValidators.isTimeValid()]],
    returnDate: [null as Date | null],
    returnTime: ['']
  });

  job?: ClosedownJobDetail;
  rootCauses: RootCause[] = [];
  statuses: ClosedownStatus[] = [];
  affirmations: StatusAffirmation[] = [];
  gasTypes: ClosedownOption[] = [];
  gasLeakTypes: ClosedownOption[] = [];
  equipmentStatuses: EquipmentStatus[] = [];
  assetConditions: AssetCondition[] = [];
  purchaseOrder: PurchaseOrder | null = null;
  upgrades: JobUpgrade[] = [];
  photoReasonsDisplay: string[] = [];
  photoReasons: FileNote[] = [];
  assetTagDetail: AssetTag | null = null;
  files: ClosedownFile[] = [];
  readonly maxOnsiteDate = new Date();
  readonly minReturnDate = new Date();
  photosAllowFileTypes: string[] = [];
  photoMaxSize: number = 0;
  maxSupportingDocuments: number = 0;
  maxServiceSheets: number = 0;
  fileErrorKey: string = '';  
  fileErrorParams: Record<string, string | number> = {};
  siteId: string = '';
  siteName: string = '';
  jobType: string = '';
  jobId: string = '';
  isSaving:boolean = false;
  hasLoadError: boolean = false;
  loadErrorKey: string = '';
  saveErrorKey: string = '';
  saveErrorMessage: string = '';
  saveSucceeded: boolean = false;

  // Document upload settings
  readonly allowedImageTypes = ['image/jpeg'];
  readonly reactiveJobType = 'REACTIVE';
  enableReactiveJobPhotos = false;
  enablePPMJobPhotos = false;
  jobPhotoMaxCount = 0;
  jobPhotoMaxSizeInBytes = 0;
  enableReactiveJobSupportingDocuments = false;
  enablePPMJobSupportingDocuments = false;
  noOfEnablePPMJobSupportingDocumentsAllowed = 0;
  noOfEnableReactiveJobSupportingDocumentsAllowed = 0;
  supportingDocumentAllowFileTypes: string[] = [];
  supportingDocumentAllowMaxSizeInMBs = 0;
  enableReactiveJobServiceSheets = false;
  enablePPMJobServiceSheets = false;
  noOfEnableReactiveJobServiceSheetsAllowed = 0;
  noOfEnablePPMJobServiceSheetsAllowed = 0;
  serviceSheetAllowFileTypes: string[] = [];
  serviceSheetAllowMaxSizeInMBs = 0;
  invoiceFilePrefix: string[] = []; 
  allowedSupportingDocumentFileTypesDisplay: string = '';
  allowedServiceSheetFileTypesDisplay: string = '';
  invoiceFilePrefixDisplay: string = '';
  partnerReferenceNumber: string = '';
  partnerId: string = '';
  partnerConfiguration: PartnerConfiguration | null = null;

  // Document upload
  uploadedJobFiles: FileStorageInfo[] = [];
  public selectedPhoto: FileStorageInfo | null = null;
  public isLoadingJobPhotos = false;
  
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly jobService: JobService,
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService,
    private readonly _commonFileStorageService: CommonFileStorageService,
    private _configurationService: ConfigurationService,
    private _localizationService: LocalizationService,
    private _siteApiService: SiteApiService,
    private _utilsService: UtilsService
  ) {
    this.navigationService.onChange(false, false);
  }

  get isAssetTagNotAvailable(): boolean {
    return this.closedownForm.controls.assetTag.hasError('assetTagNotAvailable');
  }

  get showGasFields(): boolean {
    return this.closedownForm.controls.gasUsed.value ?? false;
  }

  get showGasSection(): boolean {
    return !this.isTechnician || this.gasLeakTypes.length > 0;
  }

  get showReturnEta(): boolean {
    const statusId = this.closedownForm.controls.status.value;
    const statusName = this.statuses.find(status => status.id === statusId)?.name
      ?.replaceAll('-', '').replaceAll(' ', '').toUpperCase();
    return statusName === 'RETURNING' || statusName === 'AWAITINGPARTS';
  }

  get photos(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'photo');
  }

  get supportingDocuments(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'supporting-document');
  }

  get serviceSheets(): ClosedownFile[] {
    return this.files.filter(file => file.type === 'service-sheet');
  }

  get canCreateUplift(): boolean {
    return !!this.purchaseOrder?.id
      && this.purchaseOrder.upliftRequests.length === 0
      && this.purchaseOrder.uplifts.length === 0;
  }

  getDurationPart(controlName: TimePickerControlName, part: DurationPart): string {
    const [hours = '00', minutes = '00'] = this.closedownForm.controls[controlName].value?.split(':') || [];
    return part === 'hours' ? hours : minutes;
  }

  async ngOnInit(): Promise<void> {
    if (this.technicianAuth) {
      try {
        const [user, roles] = await Promise.all([
          this.technicianAuth.getUser(),
          this.technicianAuth.getUserRoles()
        ]);
        const isInternal = roles.includes(UserType.INTERNAL_USER);
        const isExternal = roles.includes(UserType.EXTERNAL_USER);
        if (!user?.emailAddress || (!isInternal && !isExternal)) {
          this.setLoadError('open_job_detail_allocation_load_error');
          return;
        }
        this.technicianAllocationType.set(isInternal ? 'internal' : 'external');
        this.technicianCompanies = user.companies ?? [];
        this.technicianAllocationForm.patchValue({
          email: user.emailAddress,
          technicianName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          phoneNumber: user.mobileNumber ?? ''
        });
      } catch {
        this.setLoadError('open_job_detail_allocation_load_error');
        return;
      }
    }

    let shouldRetrieveFromAPI = await this._configurationService.shouldRetrieveFromAPI();
    if(shouldRetrieveFromAPI){
      await this._configurationService.getAndSetLocalConfigurations(true);
    }

    await this.populateJobConfigurationSettings();

    this.siteId = this.route.snapshot.paramMap.get('siteId') || '';
    this.jobType = this.route.snapshot.paramMap.get('jobType') || '';
    this.jobId = this.route.snapshot.paramMap.get('jobId') || '';
    this.siteName = this.route.snapshot.queryParamMap.get('siteName') || '';

    if (!this.jobId || (!this.isTechnician && (!this.siteId || !this.jobType))) {
      this.setLoadError('open_job_detail_error_missing_details');
      return;
    }

    if (this.isTechnician) {
      this.closedownForm.controls.gasUsed.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(gasUsed => {
        const controls = this.closedownForm.controls;
        controls.gasType.setValidators(gasUsed ? [Validators.required] : []);
        controls.gasLeakType.setValidators(gasUsed ? [Validators.required] : []);
        controls.gasUsage.setValidators(gasUsed
          ? [Validators.required, Validators.min(0)]
          : [Validators.min(0)]);
        controls.gasType.updateValueAndValidity({ emitEvent: false });
        controls.gasLeakType.updateValueAndValidity({ emitEvent: false });
        controls.gasUsage.updateValueAndValidity({ emitEvent: false });
      });
    }

    this.closedownForm.controls.status.valueChanges.pipe(
      switchMap(statusId => statusId === null
        ? of([] as StatusAffirmation[])
        : this.jobService.getAffirmations(statusId))
    ).subscribe({
      next: affirmations => {
        this.affirmations = affirmations;
        this.closedownForm.controls.statusSubType.setValue(this.affirmations[0]?.id ?? null);
        this.updateReturnEtaValidation();

        if (!this.showSupportingDocuments()) {
          this.files = this.files.filter(
            file => file.type !== ClosedownFileTypeKey.SUPPORTING_DOCUMENT
          );
        }

        if (!this.showServiceSheets()) {
          this.files = this.files.filter(
            file => file.type !== ClosedownFileTypeKey.SERVICE_SHEET
          );
        }
      },
      error: () => {
        this.affirmations = [];
        this.closedownForm.controls.statusSubType.setValue(null);
        this.saveErrorKey = 'open_job_detail_error_status_options';
      }
    });

    const jobRequest = this.isTechnician
      ? this.jobService.getAllocatedJobById(this.jobId)
      : this.jobService.getJob(this.jobId);

    jobRequest.pipe(
      switchMap(job => {
        if (!job) {
          this.setLoadError('open_job_detail_allocated_job_unavailable');
          return of(null);
        }
        return forkJoin({
          job: of(job),
          rootCauses: this.jobService.getRootCauses(
            job.brandId || job.site?.brand?.id || '',
            job.disciplineReferenceId || '',
            job.faultServiceReferenceId || '',
            job.incidentTypeReferenceId || ''
          ),
          statuses: this.jobService.getStatuses(),
          purchaseOrder: this.isTechnician ? of(null) : this.invoiceService.getUpliftPurchaseOrder(this.jobId, job.companyId).pipe(
            catchError(() => of(null))
          ),
          gasTypes: this.jobService.getGasTypes(),
          gasLeakTypes: this.jobService.getGasLeakTypes(),
          equipmentStatus: this.jobService.getEquipmentStatuses(),
          assetConditions: this.jobService.getAssetConditions()
        });
      })
    ).subscribe({
      next: result => {
        if (!result) {
          return;
        }
        this.job = result.job;
        if (this.isTechnician && this.technicianAllocationType() === 'external') {
          const company = this.technicianCompanies.find(item => item.id === result.job.allocateCompanyId);
          this.technicianAllocationForm.controls.companyName.setValue(company?.name ?? '');
        }
        if (this.isTechnician) {
          this.siteId = result.job.siteId;
          this.jobType = result.job.jobType;
        }
        this.siteName = this.siteName || result.job.site?.name || '';
        this.rootCauses = result.rootCauses || [];
        this.statuses = result.statuses || [];
        this.gasTypes = result.gasTypes || [];
        this.gasLeakTypes = this.isTechnician
          ? result.gasLeakTypes.filter(option =>
            !!result.job.disciplineReferenceId &&
            option.disciplineReferenceId === result.job.disciplineReferenceId)
          : result.gasLeakTypes;
        this.equipmentStatuses = result.equipmentStatus || [];
        this.assetConditions = result.assetConditions || [];
        this.purchaseOrder = result.purchaseOrder;
        this.upgrades  = [];
        this.closedownForm.patchValue({ assetTag: result.job.asset?.assetTag || '' });
        this.setOnsiteToNow();

        this._siteApiService.getBrandByReferenceNumber(this.job.site.brand.referenceNumber ?? '').subscribe({
          next: async brand => {
            this.partnerReferenceNumber = brand?.partner?.referenceNumber ?? '';
            this.partnerId = brand?.partner?.id ?? '';

            this._commonFileStorageService.GetPhotoUploadReasons(this.partnerReferenceNumber).subscribe({
              next: (photoUploadReasons) => {
                this.photoReasons = photoUploadReasons || [];
                this.photoReasonsDisplay = this.photoReasons.map(reason => reason.description);
              },
              error: (error) => {
                this.setLoadError('open_job_detail_error_load_photo_reasons');
              }
            });

            try {
              this.partnerConfiguration = await firstValueFrom(this._siteApiService.getPartnerConfigurationByPartnerId(this.partnerId));
            } catch (error) {
              this.setLoadError('open_job_detail_error_load_partner_configuration');
            }

            this.isLoadingJobPhotos = true;
            try {
              this.uploadedJobFiles = await firstValueFrom(this.getJobFiles());
            } catch (error) {
              this.setLoadError('open_job_detail_error_load_job_files');
            } finally {
              this.isLoadingJobPhotos = false;
            }
          },
          error: () => {
            this.isLoadingJobPhotos = false;
            this.setLoadError('open_job_detail_error_load_details');
          }
        });
      },
      error: () => this.setLoadError('open_job_detail_error_load_details')
    });
  }

  ngOnDestroy(): void {
    this.navigationService.onChange(true, false);
    document.body.style.overflow = '';
  }

  setOnsiteToNow(): void {
    const now = new Date();
    this.closedownForm.patchValue({
      onsiteDate: now,
      onsiteTime: this.formatTime(now, this.durationMinuteInterval)
    });
  }

  isReactiveJob(): boolean {
    return this.job?.jobType.toUpperCase() === this.reactiveJobType;
  }

  removeFile(fileToRemove: ClosedownFile): void {
    this.files = this.files.filter(file => file !== fileToRemove);
  }

  clearError(): void {
    this.saveErrorKey = '';
    this.saveErrorMessage = '';
  }

  goBack(): void {
    if (this.isTechnician) {
      void this.router.navigate(['/siteinductioncompleted']);
      return;
    }
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType],
      { queryParams: { siteName: this.siteName } }
    );
  }

  openUpgrade(): void {
    if (this.isTechnician) {
      return;
    }
    void this.router.navigate(
      ['openjobs', this.siteId, this.jobType, this.jobId, 'upgrade'],
      { queryParams: { siteName: this.siteName } }
    );
  }

  async saveClosedown(): Promise<void> {
    if (this.isSaving || this.saveSucceeded) {
      return;
    }
    this.saveErrorKey = '';
    this.saveErrorMessage = '';
    this.saveSucceeded = false;
    this.closedownForm.markAllAsTouched();

    if (this.closedownForm.invalid || !this.job) {
      return;
    }

    if (this.isTechnician && (
      this.toMinutes(this.closedownForm.controls.maintenanceTime.value!) <= 0 ||
      this.toMinutes(this.closedownForm.controls.travelTime.value!) <= 0
    )) {
      this.saveErrorKey = 'open_job_detail_error_positive_duration';
      return;
    }

    const onsite = this.toDate(
      this.closedownForm.controls.onsiteDate.value!,
      this.closedownForm.controls.onsiteTime.value!
    );
    const returnEta = this.showReturnEta
      ? this.toDate(
        this.closedownForm.controls.returnDate.value!,
        this.closedownForm.controls.returnTime.value!
      )
      : undefined;

    if (!onsite || this.addDuration(onsite, this.closedownForm.controls.maintenanceTime.value!) > new Date()) {
      this.saveErrorKey = 'open_job_detail_error_future_job_time';
      return;
    }

    if (this.showReturnEta && (!returnEta || returnEta <= new Date())) {
      this.saveErrorKey = 'open_job_detail_error_future_eta';
      return;
    }

    if (this.photos.some(photo => !photo.reason)) {
      this.saveErrorKey = 'open_job_detail_error_photo_reason';
      return;
    }

    if (!this.validateRequiredDocuments()) {
      return;
    }

    this.isSaving = true;

    const uploadedFiles: FileStorageInfo[] = [];
    try {
      try {
        await this.uploadFiles(this.photos, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_photo_upload';
        throw error;
      }

      try {
        await this.uploadFiles(this.supportingDocuments, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_supporting_document_upload';
        throw error;
      }

      try {
        await this.uploadFiles(this.serviceSheets, uploadedFiles);
      } catch (error) {
        this.saveErrorKey = 'open_job_detail_error_service_sheet_upload';
        throw error;
      }

      const payload = this.buildPayload(onsite, returnEta);
      if (this.isTechnician) {
        const result = await firstValueFrom(this.jobService.saveTechnicianClosedown(payload));
        this.technicianPendingApproval = result.isPendingApproval;
      } else {
        await firstValueFrom(this.jobService.saveClosedown(payload));
      }
      this.saveSucceeded = true;
    } catch (error) {
      if(uploadedFiles.length > 0) {
        await this.deleteUploadedFiles(uploadedFiles);
      }

      if (!this.saveErrorKey) {
        this.saveErrorKey = 'open_job_detail_error_save';
      }

      this.saveErrorMessage = error instanceof HttpErrorResponse ? (error.error?.errorMessage ?? '') : '';
    } finally {
      this.isSaving = false;
    }
  }

  private async uploadFiles(files: ClosedownFile[], uploadedFiles: FileStorageInfo[]): Promise<void> {
    for (const file of files) {
      const fileRequest = await this.toFileUploadRequest(file);

      const result = await firstValueFrom(this._commonFileStorageService.UploadFile(fileRequest, file.file, this.partnerReferenceNumber));

      uploadedFiles.push(result);
    }
  }

  // #region Document upload

  protected enableJobPhotos(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.enableReactiveJobPhotos;
    }
    else 
    {
      return this.enablePPMJobPhotos;
    }
  }

  protected enableJobSupportingDocuments(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.enableReactiveJobSupportingDocuments;
    }
    else 
    {
      return this.enablePPMJobSupportingDocuments;
    }
  }

  protected enableJobServiceSheets(): boolean {
    if(this.isReactiveJob()) 
    {
      return this.partnerConfiguration?.enableReactiveJobServiceSheets ?? false;
    }
    else 
    {
      return this.partnerConfiguration?.enablePPMJobServiceSheets ?? false;
    }
  }

  private isStatusAllowed(configValue: string | null | undefined): boolean {
    if (!configValue?.trim()) {
      return true;
    }

    const selectedStatusId = this.closedownForm.controls.status.value;
    if (!selectedStatusId) {
      return false;
    }

    const selectedStatusName = this.statuses.find(x => x.id === selectedStatusId)?.name;
    if (!selectedStatusName) {
      return false;
    }

    const normalize = (value: string): string => value.toUpperCase().replace(/[^A-Z0-9]/g, '');


    return configValue
      .split(',')
      .map(status => normalize(status))
      .includes(normalize(selectedStatusName));
  }

  protected showSupportingDocuments(): boolean {
    if (!this.enableJobSupportingDocuments()) {
      return false;
    }

    const allowStatuses = this.isReactiveJob()
      ? this.partnerConfiguration?.reactiveSupportingDocumentAllowStatus
      : this.partnerConfiguration?.ppmSupportingDocumentAllowStatus;

    return this.isStatusAllowed(allowStatuses);
  }

  protected showServiceSheets(): boolean {
    if (!this.enableJobServiceSheets()) {
      return false;
    }

    const allowStatuses = this.isReactiveJob()
      ? this.partnerConfiguration?.reactiveJobServiceSheetsAllowStatus
      : this.partnerConfiguration?.ppmJobServiceSheetsAllowStatus;

    return this.isStatusAllowed(allowStatuses);
  }

  async populateJobConfigurationSettings(): Promise<void> {
    this.enableReactiveJobPhotos = await this._configurationService.getJobEnableReactiveJobPhotos();
    this.enablePPMJobPhotos = await this._configurationService.getJobEnablePPMJobPhotos();
    this.enableReactiveJobSupportingDocuments = await this._configurationService.getJobEnableReactiveJobSupportingDocuments();
    this.enablePPMJobSupportingDocuments = await this._configurationService.getJobEnablePPMJobSupportingDocuments();
    this.jobPhotoMaxCount = await this._configurationService.getJobPhotoMaxCount();
    this.jobPhotoMaxSizeInBytes = await this._configurationService.getJobPhotoMaxSizeInBytes();
    this.noOfEnablePPMJobSupportingDocumentsAllowed = await this._configurationService.getJobNoOfEnablePPMJobSupportingDocumentsAllowed();
    this.noOfEnableReactiveJobSupportingDocumentsAllowed = await this._configurationService.getJobNoOfEnableReactiveJobSupportingDocumentsAllowed();
    this.supportingDocumentAllowFileTypes = await this._configurationService.getJobSupportingDocumentAllowFileTypes();
    this.supportingDocumentAllowMaxSizeInMBs = await this._configurationService.getJobSupportingDocumentAllowMaxSizeInMBs();
    this.noOfEnableReactiveJobServiceSheetsAllowed = await this._configurationService.getJobNoOfEnableReactiveJobServiceSheetsAllowed();
    this.noOfEnablePPMJobServiceSheetsAllowed = await this._configurationService.getJobNoOfEnablePPMJobServiceSheetsAllowed();
    this.serviceSheetAllowFileTypes = await this._configurationService.getJobServiceSheetAllowFileTypes();
    this.serviceSheetAllowMaxSizeInMBs = await this._configurationService.getJobServiceSheetAllowMaxSizeInMBs();
    this.invoiceFilePrefix = await this._configurationService.getJobInvoiceFilePrefix();

    this.allowedSupportingDocumentFileTypesDisplay = this.supportingDocumentAllowFileTypes
      .map(type => type.split('/')[1])
      .join(', ');
    this.allowedServiceSheetFileTypesDisplay = this.serviceSheetAllowFileTypes
      .map(type => type.split('/')[1])
      .join(', ');
      this.invoiceFilePrefixDisplay = this.invoiceFilePrefix
      .map(prefix => prefix)
      .join(', ');
  }

  public selectFiles(event: Event, type: ClosedownFileType): void {
    const input = event.target as HTMLInputElement;

    for (const file of Array.from(input.files || [])) {
      if (!this.validateFile(file, type)) {
        continue;
      }

      this.files.push({
        file,
        type,
        reason: type === ClosedownFileTypeKey.PHOTO ? this.photoReasonsDisplay[0] || '' : undefined
      });
    }

    input.value = '';
  }

  private validateFile(file: File, type: ClosedownFileType): boolean {
    const fileAlreadyUploaded = this.files.some(
      existingFile => existingFile.file.name.toLowerCase() === file.name.toLowerCase()
    );

    if (fileAlreadyUploaded) {
      alert(this._localizationService.translate('open_job_detail_error_file_already_uploaded').replace('{fileName}', file.name));
      return false;
    }

    switch (type) {
      case ClosedownFileTypeKey.PHOTO: {
        return this.validatePhoto(file);
      }
        
      case ClosedownFileTypeKey.SUPPORTING_DOCUMENT: {
        return this.validateSupportingDocument(file);
      }

      case ClosedownFileTypeKey.SERVICE_SHEET: {
        return this.validateServiceSheet(file);
      }

      default:
        return false;
    }
  }

  private validatePhoto(file: File): boolean {
    const isValidImage = this.allowedImageTypes.includes(file.type);

    if (file.size > this.jobPhotoMaxSizeInBytes) {
      const maxSizeInMB = (this.jobPhotoMaxSizeInBytes / 1024 / 1024).toFixed(0);
      alert(this._localizationService.translate('open_job_detail_error_max_photo_size').replace('{jobPhotoMaxSize}', `${maxSizeInMB}`));

      return false;
    }
    else if (!isValidImage) {
      alert(this._localizationService.translate('open_job_detail_error_invalid_photo_type'));

      return false;
    }

    return true;
  }

  private validateSupportingDocument(file: File): boolean {
    const maxSizeInMB = this.supportingDocumentAllowMaxSizeInMBs;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      alert(this._localizationService.translate('open_job_detail_error_max_supporting_document_size').replace('{supportingDocumentMaxSize}', `${maxSizeInMB}`));

      return false;
    }
    else if (this.supportingDocumentAllowFileTypes.length > 0 && !this.supportingDocumentAllowFileTypes.includes(file.type)) {
      alert(this._localizationService.translate('open_job_detail_error_invalid_supporting_document_type').replace('{fileName}', file.name).replace('{allowedSupportingDocumentFileTypes}', this.allowedSupportingDocumentFileTypesDisplay));

      return false;
    }
    else if (this.invoiceFilePrefix.some(prefix => file.name.toUpperCase().startsWith(prefix))) {
      alert(this._localizationService.translate('open_job_detail_error_invoice_prefix_not_allowed').replace('{invoicePrefix}', this.invoiceFilePrefixDisplay));

      return false;
    }

    return true;
  }

  private validateServiceSheet(file: File): boolean {
    const maxSizeInMB = this.serviceSheetAllowMaxSizeInMBs;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      alert(this._localizationService.translate('open_job_detail_error_max_service_sheet_size').replace('{serviceSheetMaxSize}', `${maxSizeInMB}`));
      
      return false;
    }
    else if (this.serviceSheetAllowFileTypes.length > 0 && !this.serviceSheetAllowFileTypes.includes(file.type)){
      alert(this._localizationService.translate('open_job_detail_error_invalid_service_sheet_type').replace('{fileName}', file.name).replace('{allowedServiceSheetFileTypes}', this.allowedServiceSheetFileTypesDisplay));

      return false;
    }
    else if (this.invoiceFilePrefix.some(prefix => file.name.toUpperCase().startsWith(prefix))) {
      alert(this._localizationService.translate('open_job_detail_error_invoice_prefix_not_allowed').replace('{invoicePrefix}', this.invoiceFilePrefixDisplay));

      return false;
    }

    return true;
  }

  public onAddFileClick(event: MouseEvent, type: ClosedownFileType): void {
    if (!this.canAddFile(type)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  private canAddFile(type: ClosedownFileType): boolean {
    switch (type) {
      case ClosedownFileTypeKey.PHOTO: {
        return this.canAddPhoto();
      }

      case ClosedownFileTypeKey.SUPPORTING_DOCUMENT: {
        return this.canAddSupportingDocument();
      }

      case ClosedownFileTypeKey.SERVICE_SHEET: {
        return this.canAddServiceSheet();
      }

      default:
        return false;
    }
  }

  private canAddPhoto(): boolean {
    if (this.photos.length >= this.jobPhotoMaxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_photos').replace('{jobPhotoMaxCount}', `${this.jobPhotoMaxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private canAddSupportingDocument(): boolean {
    const maxCount = this.isReactiveJob() ? this.noOfEnableReactiveJobSupportingDocumentsAllowed : this.noOfEnablePPMJobSupportingDocumentsAllowed;

    if (this.supportingDocuments.length >= maxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_supporting_document_count').replace('{supportingDocumentMaxCount}', `${maxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private canAddServiceSheet(): boolean {
    const maxCount = this.isReactiveJob() ? this.noOfEnableReactiveJobServiceSheetsAllowed : this.noOfEnablePPMJobServiceSheetsAllowed;

    if (this.serviceSheets.length >= maxCount) {
      const errorMessage = this._localizationService.translate('open_job_detail_error_max_service_sheet_count').replace('{serviceSheetMaxCount}', `${maxCount}`);
      alert(errorMessage);

      return false;
    }

    return true;
  }

  private getJobFiles(): Observable<FileStorageInfo[]> {
    let referenceType: string;
    const includeBase64String = true;

    if (this.isReactiveJob()) {
      referenceType = 'JobEvent';
    } else {
      referenceType = 'PPMScheduleLineEvent';
    }

    return this._commonFileStorageService.GetFileStorageInfosByReferenceTypeAndIds(
      referenceType,
      this.job?.calloutReferenceNumber ?? '',
      this.partnerReferenceNumber,
      includeBase64String
    );
  }

  private async deleteUploadedFiles(uploadedFiles: FileStorageInfo[]): Promise<void> {
    if (uploadedFiles.length > 0) {
        for (const uploadedFile of uploadedFiles) {
          try {
            await firstValueFrom(
              this._commonFileStorageService.DeleteFileById(
                uploadedFile.id,
                this.partnerReferenceNumber
              )
            );
          } catch (error) {
          }
        }
      }
  }

  private async toFileUploadRequest(item: ClosedownFile): Promise<FileUploadRequest> {
    let fileUploadTypeCode: string;
    let fileNoteId: string | undefined;
    let referenceType: string;
    let thumbnail: string | undefined;

    if (item.type === 'photo') {
      fileUploadTypeCode = FileUploadTypeCode.PHOTO;
      thumbnail = await this._utilsService.createThumbnail(item.file);
    } 
    else if (item.type === 'supporting-document') {
      fileUploadTypeCode = FileUploadTypeCode.SUPPORTING_DOCUMENT;
    } 
    else {
      fileUploadTypeCode = FileUploadTypeCode.SERVICE_SHEET;
    }

    if (item.reason) {
      fileNoteId = this.photoReasons.find(x => x.description === item.reason)?.id;
    }
    
    if(this.isReactiveJob())
    {
      referenceType = 'JobEvent';
    }
    else
    {
      referenceType = 'PPMScheduleLineEvent';
    }

    return {
      fileName: item.file.name,
      fileUploadTypeCode: fileUploadTypeCode,
      fileExtension: '.' + item.file.name.split('.').pop()?.toLowerCase(),
      fileContentType: item.file.type,
      referenceType: referenceType,
      referenceId: this.job?.calloutReferenceNumber,
      siteId: this.job?.site.id,
      brandId: this.job?.site.brand.id,
      caption: item.file.name,
      fileNoteId,
      note: item.reason,
      thumbnail: thumbnail
    };
  }

  private validateRequiredDocuments(): boolean {
    const isSupportingDocumentsOptional = this.isReactiveJob()
      ? this.partnerConfiguration?.isReactiveJobSupportingDocumentsOptional
      : this.partnerConfiguration?.isPPMJobSupportingDocumentsOptional;

    const isServiceSheetsOptional = this.isReactiveJob()
      ? this.partnerConfiguration?.isReactiveJobServiceSheetsOptional
      : this.partnerConfiguration?.isPPMJobServiceSheetsOptional;

    const jobType = this.isReactiveJob() ? 'Reactive' : 'PPM';

    if (!isSupportingDocumentsOptional && this.supportingDocuments.length <= 0) {
      alert(this._localizationService.translate('open_job_detail_error_supporting_document_required').replace('{jobType}', jobType));
      this.saveErrorKey = '';
      return false;
    }

    if (!isServiceSheetsOptional && this.serviceSheets.length <= 0) {
      alert(this._localizationService.translate('open_job_detail_error_service_sheet_required').replace('{jobType}', jobType));
      this.saveErrorKey = '';
      return false;
    }

    return true;
  }

  // #endregion Document upload

  // #region Photo display

  protected get uploadedJobPhotos(): FileStorageInfo[] {
    return this.uploadedJobFiles.filter(file => file.fileUploadTypeCode === FileUploadTypeCode.PHOTO);
  }

  public openPhoto(photo: FileStorageInfo): void {
    this.selectedPhoto = photo;
    document.body.style.overflow = 'hidden';
  }

  public closePhoto(): void {
    this.selectedPhoto = null;
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  public onEscapePressed(): void {
    if (this.selectedPhoto) {
      this.closePhoto();
    }
  }

  // #endregion Photo display

  private updateReturnEtaValidation(): void {
    const returnDate = this.closedownForm.controls.returnDate;
    const returnTime = this.closedownForm.controls.returnTime;

    if (this.showReturnEta) {
      returnDate.addValidators(Validators.required);
      returnTime.addValidators(Validators.required);

      const now = new Date();
      returnDate.setValue(now);
      returnTime.setValue(this.formatTime(now));
    } else {
      returnDate.clearValidators();
      returnTime.clearValidators();
      returnDate.setValue(null);
      returnTime.setValue('');
    }

    returnDate.updateValueAndValidity({ emitEvent: false });
    returnTime.updateValueAndValidity({ emitEvent: false });
  }

  lookupAssetTag(assetTag: string, siteId: string): void {
    const assetTagControl = this.closedownForm.controls.assetTag;
    const trimmedAssetTag = assetTag.trim();

    if (!trimmedAssetTag || !siteId || assetTagControl.invalid) {
      return;
    }

    this.jobService.getAssetTag(siteId, trimmedAssetTag).subscribe({
      next: asset => {
        if (asset?.assetTag) {
          this.assetTagDetail = asset;
        } else {
          this.setAssetTagNotAvailable();
        }
      },
      error: () => this.setAssetTagNotAvailable()
    });
  }

  private setAssetTagNotAvailable(): void {
    const assetTagControl = this.closedownForm.controls.assetTag;
    assetTagControl.setErrors({ ...assetTagControl.errors, assetTagNotAvailable: true });
    assetTagControl.markAsTouched();
  }

  private buildPayload(onsite: Date, returnEta?: Date): ClosedownPayload {
    const values = this.closedownForm.getRawValue();
    const wasGasUsed = this.showGasSection && (values.gasUsed ?? false);

    return {
      jobId: this.jobId,
      jobEventId: this.job?.latestJobEvent?.id ?? '',
      assetId: this.isTechnician ? this.job?.asset?.assetId ?? null : this.assetTagDetail?.id ?? null,
      closedownStatusId: values.status!,
      statusAffirmationId: values.statusSubType!,
      equipmentStatusId: values.equipmentStatus,
      assetConditionId: values.assetOnDeparture,
      rootCauseId: values.rootCause!,
      onsiteDateTime: onsite.toISOString(),
      revisedETA: returnEta?.toISOString() ?? null,
      notes: values.notes || null,
      timeSpentInMinutes: this.toMinutes(values.maintenanceTime!),
      travelTimeInMinutes: this.toMinutes(values.travelTime!),
      wasGasUsed,
      gasTypeId: wasGasUsed ? values.gasType : null,
      gasUsageKg: wasGasUsed ? values.gasUsage ?? null : null,
      gasLeakTypeId: wasGasUsed ? values.gasLeakType : null,
      wasThirdPartyUsed: false,
      thirdPartyReason: null,
      isRaisedByPPM: this.jobType.toUpperCase() === JobType.PPM_JOB,
      isPendingApproval: this.isTechnician ? true : false
    };
  }

  private toMinutes(duration: string): number {
    const [hours, minutes] = duration.split(':').map(Number);
    return (hours * 60) + minutes;
  }

  private toDate(date: Date | null, time: string): Date | undefined {
    if (!date || !time) {
      return undefined;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const value = new Date(date);
    value.setHours(hours, minutes, 0, 0);
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  private addDuration(start: Date, duration: string): Date {
    const [hours, minutes] = duration.split(':').map(Number);
    return new Date(start.getTime() + ((hours * 60) + minutes) * 60_000);
  }

  private formatTime(value: Date, minuteInterval = 1): string {
    const minutes = Math.floor(value.getMinutes() / minuteInterval) * minuteInterval;
    return `${String(value.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private setLoadError(messageKey: string): void {
    this.hasLoadError = true;
    this.loadErrorKey = messageKey;
  }
}